import type { Engine } from '../types';
import { buildUrl, joinQuery } from '../url';

const PERIOD: Record<string, string> = {
  h: 'hour',
  d: 'day',
  w: 'week',
  m: 'month',
  y: 'year',
};

export const reddit: Engine = {
  id: 'reddit',
  name: 'Reddit',
  bang: 'r',
  aliases: ['reddit'],
  homepage: 'https://www.reddit.com',
  category: 'Web',
  defaultOn: true,
  description: 'Posts and comments, optionally within one subreddit.',
  params: [
    {
      id: 'subreddit',
      when: { param: 'type', in: ['comments', 'media'], orUnset: true },
      whenLabel: 'doesn’t apply when searching for communities or people',
      type: 'text',
      prefix: '@',
      pattern: /^[a-z0-9_]{2,21}$/,
      label: 'Subreddit',
      group: 'Scope',
      placeholder: 'buildapcsales',
      help: '@name searches inside r/name.',
    },
    {
      id: 'type',
      when: { param: 'viaGoogle', unset: true },
      whenLabel: 'doesn’t apply when searching via Google',
      type: 'choice',
      label: 'Type',
      group: 'Type',
      options: [
        { value: 'comments', token: 'cmts', label: 'Comments' },
        { value: 'communities', token: 'subs', label: 'Communities' },
        { value: 'media', token: 'media', label: 'Media' },
        { value: 'people', token: 'people', label: 'People' },
      ],
    },
    {
      id: 'sort',
      when: [
        { param: 'viaGoogle', unset: true },
        { param: 'type', in: ['comments'], orUnset: true },
      ],
      whenLabel:
        'doesn’t apply to communities, people, media or Google searches',
      type: 'choice',
      label: 'Sort',
      group: 'Sort',
      options: [
        { value: 'top', token: 'top', label: 'Top' },
        { value: 'new', token: 'new', label: 'New' },
        { value: 'hot', token: 'hot', label: 'Hot' },
        { value: 'comments', token: 'disc', label: 'Most comments' },
      ],
    },
    {
      id: 'time',
      when: [
        { param: 'type', in: ['comments'], orUnset: true },
        { param: 'sort', in: ['top', 'comments'], orUnset: true },
      ],
      whenLabel:
        'only applies to Top and Most comments, for posts and comments',
      type: 'period',
      label: 'Time range',
      group: 'Filters',
      units: ['h', 'd', 'w', 'm', 'y'],
      counted: false,
      help: 'Applies to Top and Most comments.',
    },
    {
      id: 'viaGoogle',
      when: [
        { param: 'type', unset: true },
        { param: 'sort', unset: true },
      ],
      whenLabel: 'can’t be combined with a Reddit type or sort',
      type: 'flag',
      token: 'gg',
      label: 'Search via Google',
      group: 'Scope',
      help: 'Google site:reddit.com — often better than Reddit search. Keeps the time range.',
    },
  ],
  build({ query, values }) {
    const sub =
      typeof values.subreddit === 'string' ? values.subreddit : undefined;
    const time = typeof values.time === 'string' ? values.time : undefined;

    if (values.viaGoogle === true) {
      return buildUrl('https://www.google.com/search', {
        q: joinQuery(
          query,
          sub ? `site:reddit.com/r/${sub}` : 'site:reddit.com'
        ),
        udm: '14',
        tbs: time && `qdr:${time}`,
      });
    }

    const base = sub
      ? `https://www.reddit.com/r/${sub}/search/`
      : 'https://www.reddit.com/search/';
    return buildUrl(base, {
      q: query,
      restrict_sr: sub ? '1' : undefined,
      type: values.type as string | undefined,
      sort: values.sort as string | undefined,
      // Without a sort, Reddit ignores t; pair it with top so it isn't lost.
      t: time && PERIOD[time],
      ...(time && !values.sort ? { sort: 'top' } : {}),
    });
  },
};
